// apps/office/src/pages/office/Quotations.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Search, Eye, Download, FileText, TrendingUp, Clock, CheckCircle,
  AlertCircle, Send, Plus, Edit, Trash2, XCircle, Mail, Loader2,
} from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';
import QuotationPDF from '../../components/office/QuotationPDF';
import EmailSendDialog from '../../components/office/EmailSendDialog';

const STATUS_FILTERS = [
  'all', 'Draft', 'Pending Approval', 'Approved', 'Rejected', 'Sent', 'Invoiced',
];

const Quotations = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [approvalAction, setApprovalAction] = useState('approve');
  const [emailQuote, setEmailQuote] = useState(null);
  const [sendingForApprovalId, setSendingForApprovalId] = useState(null);

  const isAdmin = ['admin', 'super_admin', 'manager'].includes(currentUser?.role);
  const isSales = currentUser?.role === 'sales';
  const canSendForApproval = isAdmin || isSales;

  /* ─────────────── Quotations list ─────────────── */
  const { data: quotations = [], isLoading, error, refetch } = useQuery({
    queryKey: ['quotations', 'list'],
    queryFn: () =>
      pb.collection('quotations').getFullList({
        sort: '-created',
        filter: 'isLatest = true',
        expand: 'clientJobId',
      }),
  });

  /* ─────────────── Send for Approval ─────────────── */
  const sendForApproval = useMutation({
    mutationFn: async (quote) => {
      // 1. Notify admins/managers
      try {
        const admins = await pb.collection('users').getFullList({
          filter: 'role = "admin" || role = "super_admin" || role = "manager"',
        });
        for (const admin of admins) {
          try {
            await pb.collection('notifications').create({
              userId: admin.id,
              type: 'quotation_sent',
              message: `${currentUser?.name || 'User'} sent quotation ${quote.quoteNumber} for approval.`,
              isRead: false,
            });
          } catch { /* notification failure not critical */ }
        }
      } catch { /* ignore */ }

      // 2. Update quotation status
      return pb.collection('quotations').update(quote.id, {
        status: 'Pending Approval',
        approvalStatus: 'pending',
      });
    },
    onSuccess: (_, quote) => {
      toast.success(`✅ ${quote.quoteNumber} sent for approval`);
      queryClient.invalidateQueries(['quotations']);
      refetch();
      setSendingForApprovalId(null);
    },
    onError: (err) => {
      toast.error('Failed to send: ' + err.message);
      setSendingForApprovalId(null);
    },
  });

  const handleSendForApproval = (quote) => {
    if (!window.confirm(`Send ${quote.quoteNumber} for approval?`)) return;
    setSendingForApprovalId(quote.id);
    sendForApproval.mutate(quote);
  };

  /* ─────────────── Approve / Reject ─────────────── */
  const updateStatus = useMutation({
    mutationFn: ({ id, data }) => pb.collection('quotations').update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['quotations']);
      toast.success('✅ Status updated');
      setShowApprovalDialog(false);
      setSelectedQuote(null);
      setApprovalNotes('');
      refetch();
    },
    onError: (err) => toast.error('Failed: ' + err.message),
  });

  const handleApproval = (action) => {
    if (!selectedQuote) return;
    const data = {
      status: action === 'approve' ? 'Approved' : 'Rejected',
      approvalStatus: action === 'approve' ? 'approved' : 'rejected',
      approvedBy: currentUser?.id,
      approvedAt: new Date().toISOString(),
    };
    if (approvalNotes) data.notes = approvalNotes;
    updateStatus.mutate({ id: selectedQuote.id, data });
  };

  /* ─────────────── Mark as Sent to Client ─────────────── */
  const sendToClient = useMutation({
    mutationFn: ({ id }) =>
      pb.collection('quotations').update(id, {
        status: 'Sent',
        sentToClient: true,
        sentAt: new Date().toISOString(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['quotations']);
      toast.success('Quotation marked as Sent');
      refetch();
    },
    onError: (err) => toast.error('Failed: ' + err.message),
  });

  /* ─────────────── Generate Invoice ─────────────── */
  const goToInvoiceForm = (quote) => {
    if (quote.status !== 'Approved') {
      toast.error('Only approved quotations can be invoiced');
      return;
    }
    navigate(`/office/invoices/new/${quote.id}`);
  };

  /* ─────────────── Delete ─────────────── */
  const deleteQuotation = useMutation({
    mutationFn: (id) => pb.collection('quotations').delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['quotations']);
      toast.success('Quotation deleted');
      refetch();
    },
    onError: (err) => toast.error('Delete failed: ' + err.message),
  });

  /* ─────────────── Filtering / helpers ─────────────── */
  const filteredQuotations = quotations.filter((q) => {
    const matchesSearch =
      q.quoteNumber?.toLowerCase().includes(search.toLowerCase()) ||
      q.clientName?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || q.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const config = {
      Draft: { color: 'bg-gray-100 text-gray-700', icon: Clock },
      'Pending Approval': { color: 'bg-yellow-100 text-yellow-700', icon: TrendingUp },
      Approved: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      Rejected: { color: 'bg-red-100 text-red-700', icon: AlertCircle },
      Sent: { color: 'bg-blue-100 text-blue-700', icon: Send },
      Invoiced: { color: 'bg-purple-100 text-purple-700', icon: FileText },
    };
    const { color, icon: Icon } = config[status] || config.Draft;
    return (
      <Badge className={`${color} flex items-center gap-1 w-fit`}>
        <Icon className="w-3 h-3" /> {status}
      </Badge>
    );
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', maximumFractionDigits: 0,
    }).format(price || 0);

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString('en-IN', {
          day: '2-digit', month: 'short', year: 'numeric',
        })
      : '—';

  const calculateTotals = (quote) => {
    const subtotal = quote.items?.reduce((sum, i) => sum + (i.rate || 0) * (i.quantity || 0), 0) || 0;
    const discount = quote.discountAmount || 0;
    const tax = quote.taxAmount || 0;
    return {
      subtotal,
      discountAmount: discount,
      taxAmount: tax,
      total: subtotal - discount + tax,
    };
  };

  if (isLoading) return <div className="flex justify-center items-center h-96">Loading...</div>;
  if (error) return <div className="text-center py-16 text-red-500">Error loading quotations: {error.message}</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 p-8 shadow-xl">
        <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-2">
              <FileText className="w-8 h-8" /> Quotations
            </h1>
            <p className="text-white/80">Manage all client quotations</p>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <Button
                onClick={() => navigate('/office/approvals')}
                className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30 hover:text-white gap-2"
              >
                <TrendingUp className="w-4 h-4" /> Pending Approvals
              </Button>
            )}
            <Button
              onClick={() => navigate('/office/quotation/new')}
              className="bg-white text-indigo-600 hover:bg-indigo-50 shadow-lg gap-2"
            >
              <Plus className="w-4 h-4" /> New Quotation
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><div className="flex justify-between items-center"><div><p className="text-xs text-slate-500">Total</p><p className="text-2xl font-bold">{quotations.length}</p></div><FileText className="w-8 h-8 text-indigo-500 opacity-50" /></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex justify-between items-center"><div><p className="text-xs text-slate-500">Pending Approval</p><p className="text-2xl font-bold text-yellow-600">{quotations.filter(q => q.status === 'Pending Approval').length}</p></div><TrendingUp className="w-8 h-8 text-yellow-500 opacity-50" /></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex justify-between items-center"><div><p className="text-xs text-slate-500">Approved</p><p className="text-2xl font-bold text-green-600">{quotations.filter(q => q.status === 'Approved').length}</p></div><CheckCircle className="w-8 h-8 text-green-500 opacity-50" /></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex justify-between items-center"><div><p className="text-xs text-slate-500">Total Value</p><p className="text-2xl font-bold">{formatPrice(quotations.reduce((sum, q) => sum + (q.total || 0), 0))}</p></div><FileText className="w-8 h-8 text-amber-500 opacity-50" /></div></CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-2 flex-wrap">
          {STATUS_FILTERS.map((status) => (
            <Button key={status} variant={statusFilter === status ? 'default' : 'outline'} size="sm" onClick={() => setStatusFilter(status)} className="capitalize">
              {status}
            </Button>
          ))}
        </div>
      </div>

      {/* Table */}
      <Card className="border-0 shadow-xl overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Quote #</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Approval</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredQuotations.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">No quotations found</TableCell></TableRow>
              ) : (
                filteredQuotations.map((quote) => {
                  const totals = calculateTotals(quote);
                  const isSending = sendingForApprovalId === quote.id;

                  return (
                    <TableRow key={quote.id} className="hover:bg-indigo-50/50">
                      <TableCell className="font-mono font-medium">{quote.quoteNumber}</TableCell>
                      <TableCell>
                        <p className="font-medium">{quote.clientName || '—'}</p>
                        <p className="text-xs text-slate-500">{quote.clientPhone || '—'}</p>
                      </TableCell>
                      <TableCell>{formatDate(quote.created)}</TableCell>
                      <TableCell>{quote.items?.length || 0}</TableCell>
                      <TableCell className="font-semibold">{formatPrice(totals.total)}</TableCell>

                      {/* Status */}
                      <TableCell>{getStatusBadge(quote.status)}</TableCell>

                      {/* Approval */}
                      <TableCell>
                        {quote.approvalStatus === 'pending' && <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>}
                        {quote.approvalStatus === 'approved' && <Badge className="bg-green-100 text-green-700">Approved</Badge>}
                        {quote.approvalStatus === 'rejected' && <Badge className="bg-red-100 text-red-700">Rejected</Badge>}
                        {!quote.approvalStatus && <span className="text-slate-400 text-sm">—</span>}
                      </TableCell>

                      {/* Actions */}
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1 flex-wrap">
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/office/quotation/${quote.id}`)} title="View">
                            <Eye className="w-4 h-4" />
                          </Button>

                          {(() => {
                            const locked = quote.status === 'Approved' || quote.status === 'Invoiced' || quote.approvalStatus === 'approved';
                            return quote.status === 'Draft' || (isAdmin && !locked);
                          })() && (
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/office/quotation/edit/${quote.id}`)} title="Edit">
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}

                          <PDFDownloadLink
                            document={<QuotationPDF quote={quote} items={quote.items || []} subtotal={totals.subtotal} discountAmount={totals.discountAmount} taxAmount={totals.taxAmount} total={totals.total} clientJobIdText={quote.clientJobIdText || quote.expand?.clientJobId?.jobId} />}
                            fileName={`quotation_${quote.quoteNumber}.pdf`}
                          >
                            {({ loading }) => <Button variant="ghost" size="sm" disabled={loading} title="Download PDF"><Download className="w-4 h-4" /></Button>}
                          </PDFDownloadLink>

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEmailQuote(quote)}
                            title="Email to client"
                            className="text-emerald-600 hover:text-emerald-800"
                          >
                            <Mail className="w-4 h-4" />
                          </Button>

                          {/* ─── Send for Approval (Draft only) ─── */}
                          {quote.status === 'Draft' && canSendForApproval && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSendForApproval(quote)}
                              disabled={isSending}
                              title="Send for Approval"
                              className="text-yellow-600 hover:text-yellow-800 bg-yellow-50 hover:bg-yellow-100"
                            >
                              {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
                            </Button>
                          )}

                          {/* ─── Approve / Reject (Pending Approval + admin) ─── */}
                          {isAdmin && quote.status === 'Pending Approval' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedQuote(quote);
                                  setApprovalAction('approve');
                                  setApprovalNotes('');
                                  setShowApprovalDialog(true);
                                }}
                                title="Approve"
                                className="text-green-600 hover:text-green-800 bg-green-50 hover:bg-green-100"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedQuote(quote);
                                  setApprovalAction('reject');
                                  setApprovalNotes('');
                                  setShowApprovalDialog(true);
                                }}
                                title="Reject"
                                className="text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}

                          {/* ─── Mark as Sent to Client (Approved only) ─── */}
                          {quote.status === 'Approved' && !quote.sentToClient && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => sendToClient.mutate({ id: quote.id })}
                              title="Mark as Sent to Client"
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}

                          {/* ─── Generate Invoice ─── */}
                          {isAdmin && quote.status === 'Approved' && !quote.sentToClient && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => goToInvoiceForm(quote)}
                              title="Generate Invoice"
                              className="text-purple-600 hover:text-purple-800"
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                          )}

                          {/* ─── Delete ─── */}
                          {isAdmin && quote.status !== 'Approved' && quote.status !== 'Invoiced' && quote.approvalStatus !== 'approved' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (window.confirm('Delete this quotation?')) {
                                  deleteQuotation.mutate(quote.id);
                                }
                              }}
                              title="Delete"
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{approvalAction === 'approve' ? 'Approve Quotation' : 'Reject Quotation'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              {selectedQuote && (
                <>
                  Quotation: <span className="font-semibold">{selectedQuote.quoteNumber}</span> for{' '}
                  <span className="font-semibold">{selectedQuote.clientName}</span>
                </>
              )}
            </p>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                rows={3}
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder={approvalAction === 'approve' ? 'Add approval notes...' : 'Reason for rejection...'}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApprovalDialog(false)}>Cancel</Button>
            <Button
              onClick={() => handleApproval(approvalAction)}
              disabled={updateStatus.isPending}
              className={approvalAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {updateStatus.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {approvalAction === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      {emailQuote && (
        <EmailSendDialog
          open={!!emailQuote}
          onOpenChange={(v) => !v && setEmailQuote(null)}
          documentType="quotation"
          document={emailQuote}
          pdfElement={
            <QuotationPDF
              quote={emailQuote}
              items={emailQuote.items || []}
              subtotal={emailQuote.subtotal || 0}
              discountAmount={emailQuote.discountAmount || 0}
              taxAmount={emailQuote.taxAmount || 0}
              total={emailQuote.total || 0}
              clientJobIdText={emailQuote.clientJobIdText || emailQuote.expand?.clientJobId?.jobId}
            />
          }
        />
      )}
    </div>
  );
};

export default Quotations;
